<script lang="ts">
	import LogoReferences from "@components/LogoReferences.svelte";
	import ReferenceContent from "@components/ReferenceContent.svelte";
	import Formatted from "@components/common/Formatted.svelte";
	import type { Partner } from "@core/schema/Partner.js";
	import type { Reference } from "@core/schema/Reference.js";
	import type { ReferencePlacement } from "@core/schema/ReferencePlacement.js";
	import { getGlobalTemplateProps } from "../contexts/getGlobalTemplateProps.js";

	const {
		placement,
		swapReversed,
		limit,
		startIndex = 0,
		logos,
	}: {
		placement?: ReferencePlacement;
		swapReversed?: boolean;
		limit?: number;
		startIndex?: number;
		logos?: Partner[][];
	} = $props();

	const { references } = getGlobalTemplateProps();

	const allReferences = $derived(references ?? []);
	const hasPlacementFilter = $derived(placement !== undefined);
	const logoPanels = $derived(logos ?? []);

	function getDescription(reference: Reference): string {
		if (!hasPlacementFilter) {
			return reference.defaultDescription;
		}

		const placementEntry = reference.placements.find((entry) => entry.placement === placement);
		return placementEntry?.description ?? reference.defaultDescription;
	}

	const items = $derived.by((): Reference[] => {
		const filtered = allReferences.filter(
			(reference) => !hasPlacementFilter || reference.placements.some((entry) => entry.placement === placement),
		);

		const sliced = filtered.slice(startIndex);
		return typeof limit === "number" ? sliced.slice(0, limit) : sliced;
	});

	function chunkReferencesIntoPairs(referencesToChunk: Reference[]): Reference[][] {
		const referencePairs: Reference[][] = [];

		for (let referenceIndex = 0; referenceIndex < referencesToChunk.length; referenceIndex += 2) {
			referencePairs.push(referencesToChunk.slice(referenceIndex, referenceIndex + 2));
		}

		return referencePairs;
	}

	const referencePairs = $derived.by(() => chunkReferencesIntoPairs(items));
</script>

{#each referencePairs as pair, pairIndex (pairIndex)}
	{#each pair as reference, pairItemIndex (reference.id)}
		{@const globalIndex = startIndex + pairIndex * 2 + pairItemIndex}
		<ReferenceContent {reference} reversed={globalIndex % 2 !== (swapReversed ? 1 : 0)}>
			{#snippet name()}
				<Formatted text={reference.name} />
			{/snippet}

			{#snippet desc()}
				<Formatted text={getDescription(reference)} />
			{/snippet}
		</ReferenceContent>
	{/each}

	{#if logoPanels.length > 0 && pairIndex === 0}
		<div class="full-width overflow-visible my-20">
			<LogoReferences logos={logoPanels} />
		</div>
	{/if}
{/each}
